"use client";

import { useEffect, type RefObject } from "react";
import { type PlayerRef } from "@remotion/player";

/**
 * "The show must go on": while the Player is held, the card's video and audio
 * hold too.
 *
 * Remotion 4.0.150 pauses a <Video>/<Audio> only when the composition
 * re-renders (`useMediaPlayback` reads the buffering ref at render time), and a
 * held Player doesn't re-render. So during any hold — an image loading, a
 * stalled video, the loading cat's dip to white — background video kept
 * playing. On release Remotion seeked it back to the frozen frame: the card
 * looked like it restarted and sounded like two copies out of sync (staging,
 * card 75eb68f9…, 2026-10-19).
 *
 * Pausing every media element on the Player's `waiting` event closes the gap.
 * Nothing is needed on `resume`: as frames advance again Remotion finds its
 * media paused and seeks + plays it in step.
 *
 * Remotion's shared <Audio> tags render beside the Player's root, not inside
 * it, so the scope is our wrapper around the Player. Re-check on the Remotion
 * upgrade (#779; upgrade plan risks §5): newer versions may pause on hold.
 */
export const pauseMediaIn = (scope: HTMLElement | null) => {
  scope?.querySelectorAll("video, audio").forEach((el) => {
    const media = el as HTMLMediaElement;
    if (!media.paused) media.pause();
  });
};

export const usePauseMediaWhileHeld = (
  playerRef: RefObject<PlayerRef>,
  scopeRef: RefObject<HTMLElement>,
) => {
  useEffect(() => {
    const player = playerRef.current;
    // Before the Player mounts (or a test double without events) there is
    // nothing to listen to.
    if (typeof player?.addEventListener !== "function") return;
    const onWaiting = () => pauseMediaIn(scopeRef.current);
    player.addEventListener("waiting", onWaiting);
    return () => player.removeEventListener("waiting", onWaiting);
  }, [playerRef, scopeRef]);
};
